import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, orderBy, writeBatch, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { backendConfig } from '../config/backendConfig';

// Art movement options - moved outside component to prevent recreation
const artMovements = [
  'Romanticism', 'Impressionism', 'Expressionism', 'Realism', 'Surrealism',
  'Abstract Expressionism', 'Baroque', 'Contemporary', 'Abstract Art', 'Post-Impressionism',
  'Art Nouveau', 'Conceptual Art', 'Minimalism', 'Pop Art', 'Academic Art',
  'Symbolism', 'Synthetism', 'Art Informel', 'Cubism', 'Post-Painterly Abstraction', 'Neoclassicism',
  'Pointillism', 'Fauvism', 'Neo-Suprematism',
  'Rococo', 'Neo-Expressionism', 'Op Art', 'Post-Minimalism', 'Dutch Golden Age',
  'Concrete Art (Concretism)', 'Modernism', 'Socialist Realism', 'Contemporary Realism',
  'Mannerism (Late Renaissance)', 'Northern Renaissance', 'English Renaissance', 'Naïve Art (Primitivism)',
  'Feminist Art', 'Social Realism', 'Magic Realism', 'Neo-Minimalism (Neo-Geo)', 'Naive Art', 'Spectralism',
  'Constructivism', 'Kitsch', 'High Renaissance', 'Outsider art (Art brut)',
  'Early Renaissance', 'Orientalism', 'Avant-garde', 'Tachisme', 'Tenebrism', 'Neo-Dada',
  'Photorealism (Super-Realism, Hyper-Realism)', 'Street art', 'Shōwa period (1926–1989)',
  'Naturalism', 'Modernismo', 'Light and Space', 'Futurism', 'Kinetic art',
  'Environmental Art (Land art)', 'Dada', 'Neo-Pop Art', 'Edo period (1603–1867)',
  'Pre-Raphaelite movement', 'Company School (or Company Style) of painting', 'Rajput Painting / Uniara School', 'Neo-Impressionism', 'Art Deco', 'Soviet Nonconformist Art', 'Taishō period (1912–1926)',
  'Harlem Renaissance (New Negro Movement)', 'Performance Art', 'Native Art',
  'Postcolonial art', 'Arte Povera', 'Neo-Figurative Art', 'Nouveau Réalisme',
  'Relational Art', 'Regionalism', 'Muralism', 'New European Painting', 'Pictorialism',
  'New Realism (American Realism)', 'Gothic Art', 'Tonalism', 'De Stijl (Neoplasticism)',
  'Precisionism', 'Transavantgarde', 'Meiji Period (1868–1912)', 'Arts and Crafts',
  'Neo-Romanticism', 'Spatialism', 'Classical Realism', 'Proto Renaissance',
  'New Objectivity (Neue Sachlichkeit)', 'Cubo-Futurism', 'Metaphysical art', 'CyberArt',
  'Republic of China (1912–1949)', 'Romanesque Art', 'Aestheticism', 'Junk Art',
  'Provisional Painting (New Casualism)', 'Ming Dynasty (1368–1644)', 'Young Poland',
  'Fantasy Art', 'Suprematism', 'Haute Pâte (Matter Painting)', 'Neo-Concretism',
  'Fiber Art', 'Existential Art', 'New Generation Sculpture', 'New Image Painting',
  'Neo-Orthodoxism', 'Neo-Surrealism', 'Digital Art', 'Indigenism', 'Institutional Critique',
  'Secession', 'Orphism (Simultanism)', 'Lettrism', 'Abstract Illusionism', 'Mail Art',
  'P&D (Pattern and Decoration)', 'Lowbrow Art', 'Tang Dynasty (618–907)', 'Byzantine Art', 'Islamic art',
  'Vorticism', 'Korean Informel', 'Maximalism', 'Northern Song Dynasty (960–1126)',
  'Qing Dynasty (1644–1912)', 'Muromachi period (1392–1573)', 'Safavid Period (before 1600)', 'Safavid period',
  'Dansaekhwa (Korean Monochrome Painting)', 'Excessivism', 'New Ink Art', 'New media art',
  'Purism', 'Indian Space Painting', 'Poster Art Realism', 'Stuckism', 'Yuan Dynasty (1271–1368)',
  'New Culture Movement (1915-1926)', 'Ottoman Period (after 1600)', 'People\'s Republic of China (1949-present)',
  'Classical Period (480 - 323 BC)', 'Hellenistic Period (323-31 BC)', 'Rayonism', 'Synchromism',
  'Viennese Actionism', 'Pittura Colta', 'Five Dynasties and Ten Kingdoms (907–960)',
  'Timurid Period (c.1370–1507)', 'Ottoman Period (before 1600)', 'Safavid and Qajar Periods (after 1600)',
  'Joseon Dynasty', 'Ero guro', 'Sots Art', 'Archaic Period (700-480 BC)', 'Medieval Art',
  'Coptic art', 'Black Arts Movement', 'Pre-Romanesque Art', 'Analytical Realism',
  'Transautomatism', 'Funk Art', 'Confessional Art', 'Southern Song Dynasty (1127–1279)',
  'Azuchi-Momoyama period (1573–1603)', 'Abbasid Period (750–1258)', 'Ilkhanid Period (1256–1353)',
  'Süleyman the Magnificent Period (1520–1566)', 'Mughal Period (after 1600)', 'Mozarabic', 'Art Singulier',
  'Incoherents', 'Neo-Conceptualism', 'Internet art (Net art)', 'Postinternet', 'Roman Period (30 BC - 640 AD)',
  'New Medievialism', 'Geometric Period (c.900 - c.700 BC)', 'Early Dynastic Period (3150 – 2686 BC)',
  'Old Kingdom (2686–2181 BC)', '1st Intermediate Period (2181–2055 BC)', 'Middle Kingdom (2055–1650 BC)',
  '2nd Intermediate Period (1650–1550 BC)', 'New Kingdom (1550–1069 BC)', 'Amarna Period (1351 - 1334 BC)',
  '3rd Intermediate Period (1069–664 BC)', 'Late Period (664–332 BC)', 'Ptolemaic Period (332–30 BC)',
  'Yoruba Art', 'Futuretech Art', 'Folk art', 'net.art', 'Post-classic (c.900-1580)',
  'Chicano art movement', 'Luminism', 'Shin Hanga', 'Ukiyo-e', 'Gongbi', 'Ink and wash painting'
];

const AdminArtworks = () => {
  const { user } = useAuth();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    artistName: '',
    artMovement: '',
    artCategory: '',
    timePeriod: '',
    locationCity: '',
    typeOfArtwork: '',
    levelType: '',
    artHistoryArtCompare: '',
    media: '',
    description: '',
    imageUrl: '',
    sourceLink: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [categoryAuto, setCategoryAuto] = useState(true);
  const [movementSearch, setMovementSearch] = useState('');
  const [filteredMovements, setFilteredMovements] = useState([]);
  const [showMovementDropdown, setShowMovementDropdown] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [listFilters, setListFilters] = useState({ level: '', typeOfArtwork: '', artform: '' });
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);

  // Admin status from backend (claim or email fallback)
  useEffect(() => {
    if (!user) {
      setAdminCheckLoading(false);
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${backendConfig.baseUrl}/api/admin/check-admin?uid=${user.uid}`);
        if (cancelled) return;
        if (!res.ok) {
          setIsAdmin(false);
          setAdminCheckLoading(false);
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setIsAdmin(!!data.isAdmin);
          setAdminCheckLoading(false);
        }
      } catch (_) {
        if (!cancelled) {
          setIsAdmin(false);
          setAdminCheckLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Type of artwork options
  const artworkTypes = [
    'Portrait', 'Landscape', 'Still Life', 'Abstract', 'Sacred',
    'Genre Scene', 'Self-Portrait', 'Architectural', 'Animal/Birds', 'Fantasy',
    'Study/Sketch', 'Miniature', 'Calligraphy', 'Sculpture', 'Interior',
    'Seascape', 'Floral', 'Objects'
  ];

  // Media options
  const mediaOptions = [
    'Oil on canvas', 'Watercolor', 'Acrylic on canvas', 'Tempera', 'Fresco',
    'Gouache', 'Ink on paper', 'Charcoal', 'Pencil', 'Pastel',
    'Mixed media', 'Digital', 'Photography', 'Sculpture - Bronze', 'Sculpture - Marble',
    'Sculpture - Wood', 'Sculpture - Stone', 'Sculpture - Clay', 'Print - Etching',
    'Print - Lithograph', 'Print - Woodcut', 'Print - Screen print', 'Installation',
    'Performance', 'Video', 'Textile', 'Ceramic', 'Glass', 'Metal',
    'Paper', 'Collage', 'Assemblage', 'Other'
  ];

  // High-level art categories for grouping movements (used for filtering in Compare and tagging in admin)
  const artCategories = [
    'Ancient Egyptian Art',
    'Ancient Greek Art',
    'Western Medieval Art',
    'Western Renaissance Art',
    'Western Post Renaissance Art',
    'Modern Art',
    'Contemporary Art',
    'Chinese Art',
    'Korean Art',
    'Japanese Art',
    'Islamic Art',
    'Native Art',
    'Pre-Columbian Art'
  ];

  // Auto-map movements to categories (same logic as selection modal)
  const movementToCategory = (movement) => {
    if (!movement) return '';
    const m = movement.toLowerCase();
    if (['ancient egyptian art','early dynastic period','old kingdom','1st intermediate period','middle kingdom','2nd intermediate period','new kingdom','amarna period','3rd intermediate period','late period','ptolemaic period','roman period'].some(k=>m.includes(k))) return 'Ancient Egyptian Art';
    if (['ancient greek art','geometric period','archaic period','classical period','hellenistic period'].some(k=>m.includes(k))) return 'Ancient Greek Art';
    if (['byzantine','pre-romaneseque','romanesque','gothic','medieval','coptic'].some(k=>m.includes(k))) return 'Western Medieval Art';
    if (['proto renaissance','early renaissance','high renaissance','mannerism','northern renaissance'].some(k=>m.includes(k))) return 'Western Renaissance Art';
    if (['baroque','dutch golden age','rococo','neoclassicism','romanticism','orientalism','academic art','realism'].some(k=>m.includes(k))) return 'Western Post Renaissance Art';
    if (['naturalism','symbolism','impressionism','post-impressionism','pointillism','fauvism','neo-suprematism','naive art','naïve art','spectralism','modern','cubism','abstract','expressionism','futurism','dada','surrealism','constructivism','de stijl','bauhaus','art deco','suprematism','vorticism','precisionism','purism','rayonism','metaphysical'].some(k=>m.includes(k))) return 'Modern Art';
    if (['contemporary','minimalism','neo-minimalism','conceptual','neo-conceptualism','installation','pop art','op art','performance','digital','postinternet','street art','lowbrow','relational'].some(k=>m.includes(k))) return 'Contemporary Art';
    if (['tang dynasty','five dynasties','song dynasty','yuan dynasty','ming dynasty','qing dynasty','republic of china','new culture movement','people\'s republic of china','ink'].some(k=>m.includes(k))) return 'Chinese Art';
    if (['joseon','korean','dansaekhwa','informel'].some(k=>m.includes(k))) return 'Korean Art';
    if (['muromachi','azuchi-momoyama','edo','meiji','taishō','taisho','shōwa','showa','japanese'].some(k=>m.includes(k))) return 'Japanese Art';
    if (['abbasid','ilkhanid','timurid','ottoman','safavid','mughal','qajar','islamic'].some(k=>m.includes(k))) return 'Islamic Art';
    if (['native','yoruba','folk'].some(k=>m.includes(k))) return 'Native Art';
    if (['pre-columbian','post-classic'].some(k=>m.includes(k))) return 'Pre-Columbian Art';
    return '';
  };

  useEffect(() => {
    if (isAdmin) {
      fetchArtworks();
    }
  }, [isAdmin]);

  // Initialize filtered movements and filter based on search
  useEffect(() => {
    if (movementSearch.trim() === '') {
      setFilteredMovements(artMovements);
      setShowMovementDropdown(false);
    } else {
      const filtered = artMovements.filter(movement =>
        movement.toLowerCase().includes(movementSearch.toLowerCase())
      );
      setFilteredMovements(filtered);
      setShowMovementDropdown(true);
    }
  }, [movementSearch]); // Removed artMovements from dependencies

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMovementDropdown && !event.target.closest('.movement-dropdown')) {
        setShowMovementDropdown(false);
        setMovementSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMovementDropdown]);

  const fetchArtworks = async () => {
    try {
      setLoading(true);
      console.log('🔄 [ADMIN] Fetching artworks from Firebase...');
      
      // Try with orderBy first (for artworks with createdAt)
      let querySnapshot;
      try {
        const q = query(collection(db, 'artHistoryArtworks'), orderBy('createdAt', 'desc'));
        querySnapshot = await getDocs(q);
        console.log('✅ [ADMIN] Query with orderBy succeeded:', querySnapshot.docs.length, 'documents');
      } catch (orderByError) {
        console.warn('⚠️ [ADMIN] orderBy query failed, trying without orderBy:', orderByError);
        // Fallback: fetch without orderBy if createdAt index is missing
        const q = query(collection(db, 'artHistoryArtworks'));
        querySnapshot = await getDocs(q);
        console.log('✅ [ADMIN] Query without orderBy succeeded:', querySnapshot.docs.length, 'documents');
      }
      
      const artworksData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Ensure createdAt exists (use updatedAt or current time as fallback)
        if (!data.createdAt) {
          data.createdAt = data.updatedAt || new Date();
        }
        return {
          id: doc.id,
          ...data
        };
      });
      
      // Sort by createdAt if available (client-side fallback)
      artworksData.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.getTime ? a.createdAt.getTime() : 0);
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.getTime ? b.createdAt.getTime() : 0);
        return bTime - aTime; // Descending order
      });
      
      console.log('✅ [ADMIN] Fetched artworks:', artworksData.length);
      if (artworksData.length > 0) {
        console.log('📋 [ADMIN] Sample artwork:', artworksData[0]);
      } else {
        console.warn('⚠️ [ADMIN] No artworks found in collection!');
      }
      
      setArtworks(artworksData);
    } catch (error) {
      console.error('❌ [ADMIN] Error fetching artworks:', error);
      console.error('Error details:', error.message, error.code);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'artCategory') {
      setCategoryAuto(false);
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  useEffect(() => {
    if (!formData.artMovement) return;
    if (!categoryAuto) return;
    const auto = movementToCategory(formData.artMovement);
    if (auto && formData.artCategory !== auto) {
      setFormData(prev => ({ ...prev, artCategory: auto }));
    }
  }, [formData.artMovement, categoryAuto]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setErrors(prev => ({
        ...prev,
        imageUrl: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.artistName.trim()) newErrors.artistName = 'Artist name is required';
    if (!imageFile && !formData.imageUrl) newErrors.imageUrl = 'Image is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadImage = async (file) => {
    try {
      // Use the existing artworks folder structure that has proper permissions
      const storageRef = ref(storage, `artworks/${user.uid}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setUploading(true);
      
      let imageUrl = formData.imageUrl;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      const detectedCategory = formData.artCategory || movementToCategory(formData.artMovement);
      // Normalize legacy type labels
      let typeOfArtwork = formData.typeOfArtwork;
      if (typeOfArtwork === 'Cityscape') {
        typeOfArtwork = 'Architectural';
      }
      if (typeOfArtwork === 'Flower painting' || typeOfArtwork === 'Flower Painting') {
        typeOfArtwork = 'Floral';
      }
      if (typeOfArtwork === 'Animal' || typeOfArtwork === 'Animals' || typeOfArtwork === 'Animal / Birds') {
        typeOfArtwork = 'Animal/Birds';
      }
      
      const artworkData = {
        ...formData,
        typeOfArtwork: typeOfArtwork,
        artCategory: detectedCategory || '',
        imageUrl,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (editingId) {
        // Update existing artwork
        await updateDoc(doc(db, 'artHistoryArtworks', editingId), {
          ...artworkData,
          updatedAt: serverTimestamp()
        });
        setEditingId(null);
      } else {
        // Add new artwork
        await addDoc(collection(db, 'artHistoryArtworks'), artworkData);
      }

      // Reset form
      setFormData({
        title: '',
        artistName: '',
        artMovement: '',
        artCategory: '',
        timePeriod: '',
        locationCity: '',
        typeOfArtwork: '',
        levelType: '',
        artHistoryArtCompare: '',
        media: '',
        description: '',
        imageUrl: '',
        sourceLink: ''
      });
      setImageFile(null);
      setErrors({});
      setMovementSearch('');
      setShowMovementDropdown(false);
      setCategoryAuto(true);
      
      // Refresh list
      await fetchArtworks();
      
    } catch (error) {
      console.error('Error saving artwork:', error);
      alert('Error saving artwork. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Derived list for Artworks Collection section with simple filters by level + artwork type
  const levelFilterOptions = ['Level 1', 'Level 2', 'Level 3', 'Not Specified'];

  const filteredArtworksList = artworks.filter((artwork) => {
    const level = artwork.levelType || 'Not Specified';
    let type = artwork.typeOfArtwork || 'Not Specified';
    // Normalize legacy labels so filters behave as expected
    if (type === 'Cityscape') type = 'Architectural';
    if (type === 'Flower painting' || type === 'Flower Painting') type = 'Floral';
    if (type === 'Animal' || type === 'Animals' || type === 'Animal / Birds') type = 'Animal/Birds';

    const matchesLevel = !listFilters.level || level === listFilters.level;
    const matchesType = !listFilters.typeOfArtwork || type === listFilters.typeOfArtwork;
    const matchesArtform = !listFilters.artform || (artwork.artMovement && artwork.artMovement === listFilters.artform);
    return matchesLevel && matchesType && matchesArtform;
  });

  const handleEdit = (artwork) => {
    // Normalize legacy type labels for editing
    let typeOfArtwork = artwork.typeOfArtwork || '';
    if (typeOfArtwork === 'Cityscape') {
      typeOfArtwork = 'Architectural';
    }
    if (typeOfArtwork === 'Flower painting' || typeOfArtwork === 'Flower Painting') {
      typeOfArtwork = 'Floral';
    }
    if (typeOfArtwork === 'Animal' || typeOfArtwork === 'Animals' || typeOfArtwork === 'Animal / Birds') {
      typeOfArtwork = 'Animal/Birds';
    }
    
    setFormData({
      title: artwork.title || '',
      artistName: artwork.artistName || '',
      artMovement: artwork.artMovement || '',
      artCategory: artwork.artCategory || '',
      timePeriod: artwork.timePeriod || '',
      locationCity: artwork.locationCity || '',
      typeOfArtwork: typeOfArtwork,
      levelType: artwork.levelType || '',
      artHistoryArtCompare: artwork.artHistoryArtCompare || '',
      media: artwork.media || '',
      description: artwork.description || '',
      imageUrl: artwork.imageUrl || '',
      sourceLink: artwork.sourceLink || ''
    });
    setEditingId(artwork.id);
    setCategoryAuto(!artwork.artCategory);
    setImageFile(null);
    setErrors({});
    setMovementSearch('');
    setShowMovementDropdown(false);
    setCategoryAuto(true);
    
    // Scroll to form
    document.getElementById('artwork-form').scrollIntoView({ behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this artwork?')) {
      try {
        await deleteDoc(doc(db, 'artHistoryArtworks', id));
        await fetchArtworks();
      } catch (error) {
        console.error('Error deleting artwork:', error);
        alert('Error deleting artwork. Please try again.');
      }
    }
  };

  const handleAcceptCorrection = async (artwork) => {
    if (!artwork?.id) return;
    const correctedMovement = String(artwork.correctedArtMovement || '').trim();
    const correctedStyle = String(artwork.correctedArtStyle || '').trim();
    if (!correctedMovement) {
      alert('No corrected art movement available to accept.');
      return;
    }
    try {
      await updateDoc(doc(db, 'artHistoryArtworks', artwork.id), {
        currentArtMovement: correctedMovement,
        currentStyle: correctedStyle || artwork.currentStyle || artwork.style || '',
        artMovement: correctedMovement,
        styleCorrectionStatus: 'accepted',
        styleAcceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await fetchArtworks();
    } catch (error) {
      console.error('Error accepting style correction:', error);
      alert('Failed to accept correction.');
    }
  };

  const handleRejectCorrection = async (artwork) => {
    if (!artwork?.id) return;
    const reason = window.prompt('Optional rejection reason:', '') || '';
    try {
      await updateDoc(doc(db, 'artHistoryArtworks', artwork.id), {
        styleCorrectionStatus: 'rejected',
        styleRejectedReason: reason.trim(),
        styleRejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await fetchArtworks();
    } catch (error) {
      console.error('Error rejecting style correction:', error);
      alert('Failed to reject correction.');
    }
  };

  const handleManualStyleEdit = async (artwork) => {
    if (!artwork?.id) return;
    const nextMovement = window.prompt('Enter manual Art Movement', artwork.currentArtMovement || artwork.artMovement || '') || '';
    if (!nextMovement.trim()) return;
    const nextStyle = window.prompt('Enter manual Style (optional)', artwork.currentStyle || artwork.style || '') || '';
    try {
      await updateDoc(doc(db, 'artHistoryArtworks', artwork.id), {
        currentArtMovement: nextMovement.trim(),
        currentStyle: nextStyle.trim(),
        artMovement: nextMovement.trim(),
        correctedArtMovement: nextMovement.trim(),
        correctedArtStyle: nextStyle.trim(),
        styleCorrectionStatus: 'manual',
        styleManuallyEditedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await fetchArtworks();
    } catch (error) {
      console.error('Error applying manual style edit:', error);
      alert('Failed to apply manual style edit.');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      title: '',
      artistName: '',
      artMovement: '',
      artCategory: '',
      timePeriod: '',
      locationCity: '',
      typeOfArtwork: '',
      levelType: '',
      artHistoryArtCompare: '',
      media: '',
      description: '',
      imageUrl: '',
      sourceLink: ''
    });
    setImageFile(null);
    setErrors({});
    setMovementSearch('');
    setShowMovementDropdown(false);
  };

  const handleMigration = async () => {
    if (!window.confirm('This will update all existing artworks:\n1. Convert Cityscape to Architectural\n2. Distribute artworks without levels evenly across Level 1, 2, and 3\n\nContinue?')) {
      return;
    }

    try {
      setMigrating(true);
      console.log('🔄 Starting migration...');

      // Fetch all artworks
      const querySnapshot = await getDocs(collection(db, 'artHistoryArtworks'));
      const allArtworks = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`📊 Found ${allArtworks.length} artworks to process`);

      // Separate artworks that need updates
      const artworksToUpdate = [];
      const artworksWithoutLevel = [];

      allArtworks.forEach(artwork => {
        let needsUpdate = false;
        const updates = {};

        // Check for Cityscape -> Architectural conversion
        if (artwork.typeOfArtwork === 'Cityscape') {
          updates.typeOfArtwork = 'Architectural';
          needsUpdate = true;
        }
        // Check for Flower painting -> Floral conversion
        if (artwork.typeOfArtwork === 'Flower painting' || artwork.typeOfArtwork === 'Flower Painting') {
          updates.typeOfArtwork = 'Floral';
          needsUpdate = true;
        }
        // Check for Animal -> Animal/Birds conversion
        if (artwork.typeOfArtwork === 'Animal' || artwork.typeOfArtwork === 'Animals' || artwork.typeOfArtwork === 'Animal / Birds') {
          updates.typeOfArtwork = 'Animal/Birds';
          needsUpdate = true;
        }

        // Check for missing level
        if (!artwork.levelType || artwork.levelType === '') {
          artworksWithoutLevel.push(artwork);
        } else if (needsUpdate) {
          artworksToUpdate.push({ id: artwork.id, updates });
        }

        if (needsUpdate && artwork.levelType) {
          artworksToUpdate.push({ id: artwork.id, updates });
        }
      });

      console.log(`🏙️ Found ${artworksToUpdate.length} artworks with Cityscape to convert`);
      console.log(`📋 Found ${artworksWithoutLevel.length} artworks without levels`);

      // Distribute artworks without levels evenly across Level 1, 2, 3
      const levels = ['Level 1', 'Level 2', 'Level 3'];
      artworksWithoutLevel.forEach((artwork, index) => {
        const level = levels[index % 3];
        const updates = { levelType: level };
        
        // Also check for Cityscape / Flower painting / Animal conversions
        if (artwork.typeOfArtwork === 'Cityscape') {
          updates.typeOfArtwork = 'Architectural';
        }
        if (artwork.typeOfArtwork === 'Flower painting' || artwork.typeOfArtwork === 'Flower Painting') {
          updates.typeOfArtwork = 'Floral';
        }
        if (artwork.typeOfArtwork === 'Animal' || artwork.typeOfArtwork === 'Animals' || artwork.typeOfArtwork === 'Animal / Birds') {
          updates.typeOfArtwork = 'Animal/Birds';
        }

        artworksToUpdate.push({ id: artwork.id, updates });
      });

      console.log(`✅ Total artworks to update: ${artworksToUpdate.length}`);

      // Update artworks in batches (Firestore batch limit is 500)
      const batchSize = 500;
      let updatedCount = 0;

      for (let i = 0; i < artworksToUpdate.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchItems = artworksToUpdate.slice(i, i + batchSize);

        batchItems.forEach(({ id, updates }) => {
          const artworkRef = doc(db, 'artHistoryArtworks', id);
          batch.update(artworkRef, {
            ...updates,
            updatedAt: new Date()
          });
        });

        await batch.commit();
        updatedCount += batchItems.length;
        console.log(`✅ Updated ${updatedCount}/${artworksToUpdate.length} artworks`);
      }

      alert(`Migration completed successfully!\n\n- Updated ${artworksToUpdate.length} artworks\n- Cityscape → Architectural conversions\n- Levels assigned to artworks without levels`);
      
      // Refresh the artworks list
      await fetchArtworks();
    } catch (error) {
      console.error('❌ Migration error:', error);
      alert('Error during migration: ' + error.message);
    } finally {
      setMigrating(false);
    }
  };

  if (!isAdmin && !adminCheckLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to access this admin panel.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {adminCheckLoading && (
          <div className="mb-4 rounded-xl border border-purple-200 bg-white/85 px-4 py-3 text-sm text-purple-800">
            Checking admin access...
          </div>
        )}
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Art History Admin Panel</h1>
          <p className="text-gray-600 text-lg">Manage and upload artworks to the Artmosphere collection</p>
          <button
            onClick={handleMigration}
            disabled={migrating}
            className="mt-4 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {migrating ? 'Migrating...' : 'Run Migration (Cityscape → Architectural & Assign Levels)'}
          </button>
        </div>

        {/* Upload Form */}
        <div id="artwork-form" className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {editingId ? 'Edit Artwork' : 'Add New Artwork'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                    errors.title ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="Enter artwork title"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              {/* Artist Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Artist Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="artistName"
                  value={formData.artistName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                    errors.artistName ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="Enter artist name"
                />
                {errors.artistName && <p className="text-red-500 text-sm mt-1">{errors.artistName}</p>}
              </div>

              {/* Art Movement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Art Movement
                </label>
                <div className="relative movement-dropdown">
                  <input
                    type="text"
                    placeholder="Search art movements..."
                    value={movementSearch}
                    onChange={(e) => setMovementSearch(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all mb-2"
                    onFocus={() => {
                      if (movementSearch.trim() === '') {
                        setMovementSearch('');
                      }
                    }}
                  />
                  {showMovementDropdown && movementSearch && (
                    <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
                      {filteredMovements.length > 0 ? (
                        filteredMovements.slice(0, 20).map(movement => (
                          <div
                            key={movement}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const autoCat = movementToCategory(movement);
                              setFormData(prev => ({ ...prev, artMovement: movement, artCategory: autoCat || prev.artCategory }));
                              setCategoryAuto(true);
                              setMovementSearch('');
                              setShowMovementDropdown(false);
                            }}
                            className="px-4 py-2 hover:bg-purple-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                          >
                            {movement}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          No movements found
                        </div>
                      )}
                    </div>
                  )}
                  <select
                    name="artMovement"
                    value={formData.artMovement}
                    onChange={(e) => {
                      const value = e.target.value;
                      const auto = movementToCategory(value);
                      setFormData(prev => ({ ...prev, artMovement: value, artCategory: categoryAuto ? (auto || prev.artCategory) : prev.artCategory }));
                    }}
                    className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                      errors.artMovement ? 'border-red-300' : 'border-gray-200'
                    }`}
                  >
                    <option value="">Select art movement</option>
                    {artMovements.map(movement => (
                      <option key={movement} value={movement}>{movement}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Art Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Art Category</label>
                <select
                  name="artCategory"
                  value={formData.artCategory}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  <option value="">Select category</option>
                  {artCategories.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>


              {/* Time Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Period</label>
                <input
                  type="text"
                  name="timePeriod"
                  value={formData.timePeriod}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  placeholder="e.g., 1889, Renaissance, 20th Century"
                />
              </div>

              {/* Location City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location/City</label>
                <input
                  type="text"
                  name="locationCity"
                  value={formData.locationCity}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  placeholder="e.g., Paris, Florence, New York"
                />
              </div>

              {/* Type of Artwork */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type of Artwork</label>
                <select
                  name="typeOfArtwork"
                  value={formData.typeOfArtwork}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  <option value="">Select type</option>
                  {artworkTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Level Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Level Type</label>
                <select
                  name="levelType"
                  value={formData.levelType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  <option value="">Select level</option>
                  <option value="Level 1">Level 1</option>
                  <option value="Level 2">Level 2</option>
                  <option value="Level 3">Level 3</option>
                </select>
              </div>

              {/* Art history / Art Compare */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Art history / Art Compare?</label>
                <select
                  name="artHistoryArtCompare"
                  value={formData.artHistoryArtCompare}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  <option value="">Select option</option>
                  <option value="Art history">Art history</option>
                  <option value="Art compare">Art compare</option>
                  <option value="Both">Both</option>
                </select>
              </div>

              {/* Media */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Media</label>
                <select
                  name="media"
                  value={formData.media}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  <option value="">Select media</option>
                  {mediaOptions.map(media => (
                    <option key={media} value={media}>{media}</option>
                  ))}
                </select>
              </div>

              {/* Source Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Source Link</label>
                <input
                  type="url"
                  name="sourceLink"
                  value={formData.sourceLink}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  placeholder="https://example.com"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="Enter artwork description..."
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image <span className="text-red-500">*</span>
              </label>
              <div className="space-y-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                    errors.imageUrl ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                {!imageFile && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Or enter image URL</label>
                    <input
                      type="url"
                      name="imageUrl"
                      value={formData.imageUrl}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                        errors.imageUrl ? 'border-red-300' : 'border-gray-200'
                      }`}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                )}
                {errors.imageUrl && <p className="text-red-500 text-sm mt-1">{errors.imageUrl}</p>}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={uploading}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-3 px-6 rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Saving...' : editingId ? 'Update Artwork' : 'Add Artwork'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 bg-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-300 transition-all duration-300"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Statistics Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Artwork Statistics by Level</h2>
          
          {(() => {
            // Calculate statistics grouped by level and artwork type
            const stats = {};
            artworks.forEach(artwork => {
              const level = artwork.levelType || 'Not Specified';
              let type = artwork.typeOfArtwork || 'Not Specified';
              
              // Normalize legacy type labels for stats display
              if (type === 'Cityscape') {
                type = 'Architectural';
              }
              if (type === 'Flower painting' || type === 'Flower Painting') {
                type = 'Floral';
              }
              if (type === 'Animal' || type === 'Animals' || type === 'Animal / Birds') {
                type = 'Animal/Birds';
              }
              
              if (!stats[level]) {
                stats[level] = {};
              }
              if (!stats[level][type]) {
                stats[level][type] = 0;
              }
              stats[level][type]++;
            });

            const levels = ['Level 1', 'Level 2', 'Level 3', 'Not Specified'];
            
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {levels.map(level => {
                  const levelStats = stats[level] || {};
                  const totalCount = Object.values(levelStats).reduce((sum, count) => sum + count, 0);
                  
                  if (totalCount === 0 && level !== 'Not Specified') return null;
                  
                  return (
                    <div key={level} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-100">
                      <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center justify-between">
                        <span>{level}</span>
                        <span className="text-purple-600 bg-white px-3 py-1 rounded-full text-sm">
                          {totalCount} total
                        </span>
                      </h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {Object.keys(levelStats).length > 0 ? (
                          Object.entries(levelStats)
                            .sort((a, b) => b[1] - a[1]) // Sort by count descending
                            .map(([type, count]) => (
                              <div key={type} className="flex justify-between items-center bg-white rounded-lg px-3 py-2 shadow-sm">
                                <span className="text-sm text-gray-700 truncate flex-1">{type}</span>
                                <span className="text-sm font-bold text-purple-600 ml-2">{count}</span>
                              </div>
                            ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">No artworks</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Used Art Movements Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Art Movements Used in Uploaded Artworks</h2>
          
          {(() => {
            // Extract unique art movements from uploaded artworks
            const movementCounts = {};
            artworks.forEach(artwork => {
              const movement = artwork.artMovement;
              if (movement && movement.trim() !== '') {
                movementCounts[movement] = (movementCounts[movement] || 0) + 1;
              }
            });
            
            const usedMovements = Object.keys(movementCounts).sort();
            const sortedByCount = Object.entries(movementCounts)
              .sort((a, b) => b[1] - a[1]);
            
            if (usedMovements.length === 0) {
              return (
                <div className="text-center py-8">
                  <p className="text-gray-600">No art movements found in uploaded artworks.</p>
                </div>
              );
            }
            
            return (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border-2 border-purple-100">
                  <p className="text-lg font-semibold text-gray-800">
                    Total Unique Art Movements: <span className="text-purple-600">{usedMovements.length}</span>
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-bold text-gray-700 mb-4">Sorted by Usage Count:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                    {sortedByCount.map(([movement, count]) => (
                      <div key={movement} className="flex justify-between items-center bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
                        <span className="text-sm text-gray-700 truncate flex-1">{movement}</span>
                        <span className="text-sm font-bold text-purple-600 ml-3 bg-white px-2 py-1 rounded-full">
                          {count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-bold text-gray-700 mb-4">Alphabetical List:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-96 overflow-y-auto">
                    {usedMovements.map((movement, index) => (
                      <div key={movement} className="flex items-center bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
                        <span className="text-xs text-gray-500 mr-2 w-8">{(index + 1).toString().padStart(3, ' ')}.</span>
                        <span className="text-sm text-gray-700">{movement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Artworks List */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Artworks Collection</h2>
              <span className="text-gray-600 text-sm">
                Showing {filteredArtworksList.length} of {artworks.length} artworks
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Filter by level</label>
                <select
                  value={listFilters.level}
                  onChange={(e) => setListFilters((prev) => ({ ...prev, level: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                >
                  <option value="">All levels</option>
                  {levelFilterOptions.map((lvl) => (
                    <option key={lvl} value={lvl}>{lvl}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Filter by type</label>
                <select
                  value={listFilters.typeOfArtwork}
                  onChange={(e) => setListFilters((prev) => ({ ...prev, typeOfArtwork: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                >
                  <option value="">All types</option>
                  {artworkTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                  <option value="Not Specified">Not Specified</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Filter by artform</label>
                <select
                  value={listFilters.artform}
                  onChange={(e) => setListFilters((prev) => ({ ...prev, artform: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[180px]"
                >
                  <option value="">All artforms</option>
                  {(() => {
                    // Get unique art movements from artworks
                    const uniqueArtforms = [...new Set(artworks.map(a => a.artMovement).filter(Boolean))].sort();
                    return uniqueArtforms.map((artform) => (
                      <option key={artform} value={artform}>{artform}</option>
                    ));
                  })()}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
              <p className="text-gray-600 mt-2">Loading artworks...</p>
            </div>
          ) : artworks.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-gray-600">No artworks found. Add your first artwork above!</p>
            </div>
          ) : filteredArtworksList.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No artworks match the selected level/type filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArtworksList.map((artwork) => (
                <div key={artwork.id} className="bg-gray-50 rounded-xl p-4 hover:shadow-lg transition-all duration-300">
                  <div className="aspect-square bg-gray-200 rounded-lg mb-4 overflow-hidden">
                    <img
                      src={artwork.imageUrl}
                      alt={artwork.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzljYTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIE5vdCBGb3VuZDwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-bold text-gray-800 truncate">{artwork.title}</h3>
                    <p className="text-gray-600 text-sm truncate">{artwork.artistName}</p>
                    {artwork.artMovement && (
                      <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                        {artwork.artMovement}
                      </span>
                    )}
                    {(artwork.correctedArtMovement || artwork.correctedArtStyle) && (
                      <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs">
                        <p className="font-semibold text-emerald-800">AI Correction</p>
                        <p className="text-emerald-900">
                          Movement: <span className="font-medium">{artwork.correctedArtMovement || '-'}</span>
                        </p>
                        <p className="text-emerald-900">
                          Style: <span className="font-medium">{artwork.correctedArtStyle || '-'}</span>
                        </p>
                        <p className="text-emerald-800">
                          Confidence: <span className="font-semibold">{artwork.styleConfidence || 'LOW'}</span>
                          {artwork.styleNeedsHumanReview ? ' · Needs human review' : ''}
                        </p>
                        {artwork.styleValidationReason && (
                          <p className="text-emerald-700 mt-1 line-clamp-3">{artwork.styleValidationReason}</p>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => handleEdit(artwork)}
                        className="flex-1 bg-blue-500 text-white text-sm font-medium py-2 px-3 rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(artwork.id)}
                        className="flex-1 bg-red-500 text-white text-sm font-medium py-2 px-3 rounded-lg hover:bg-red-600 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                    {(artwork.correctedArtMovement || artwork.correctedArtStyle) && (
                      <div className="mt-2 grid grid-cols-1 gap-2">
                        <button
                          onClick={() => handleAcceptCorrection(artwork)}
                          className="bg-emerald-600 text-white text-xs font-medium py-2 px-3 rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          Accept correction
                        </button>
                        <button
                          onClick={() => handleRejectCorrection(artwork)}
                          className="bg-amber-500 text-white text-xs font-medium py-2 px-3 rounded-lg hover:bg-amber-600 transition-colors"
                        >
                          Reject correction
                        </button>
                        <button
                          onClick={() => handleManualStyleEdit(artwork)}
                          className="bg-indigo-500 text-white text-xs font-medium py-2 px-3 rounded-lg hover:bg-indigo-600 transition-colors"
                        >
                          Edit manually
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminArtworks;